import React, { useState, useMemo } from 'react';
import { Modal, Button, Table, Badge } from 'react-bootstrap';

const VMStatusModal = ({ status, failed_instances, instances, nonExisting }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Parse status to extract ratio
  const statusMatch = status?.match(/\((\d+)\/(\d+)\)/);
  const hasRatio = statusMatch && statusMatch.length === 3;
  
  const succeeded = hasRatio ? parseInt(statusMatch[1]) : 0;
  const total = hasRatio ? parseInt(statusMatch[2]) : 0;
  const failed = total - succeeded;

  // Recalculate VM statuses when props change
  const vmStatuses = useMemo(() => {
    // console.log('VMStatusModal recalculating with:', { status, failed_instances, nonExisting });
    if (!hasRatio) return [];
    
    // Parse failed instances (handle DynamoDB format)
    let failedVMs = [];
    if (failed_instances) {
      try {
        const parsed = JSON.parse(failed_instances);
        if (parsed.L && Array.isArray(parsed.L)) {
          failedVMs = parsed.L.map(item => ({
            service_catalog_product_id: item.M?.service_catalog_product_id?.S || item.M?.label?.S || '',
            hostname: item.M?.hostname?.S || '',
            failure_reason: item.M?.failure_reason?.S || '',
            vm_label: item.M?.vm_label?.S || item.M?.provisioned_product_id?.S || ''
          }));
        } else if (Array.isArray(parsed)) {
          failedVMs = parsed;
        }
      } catch (e) {
        failedVMs = [];
      }
    }
    
    // Create status list - show failed VMs and fill remaining with succeeded VMs
    const statuses = [];
    
    // Add all failed VMs first
    failedVMs.forEach(failedVM => {
      statuses.push({
        label: failedVM.vm_label || 'Unknown',
        hostname: failedVM.hostname || 'Unknown',
        status: 'Failed',
        reason: failedVM.failure_reason || 'N/A'
      });
    });
    
    // Parse non-existing products
    const nonExistingProducts = nonExisting ? nonExisting.split(',').map(id => id.trim()) : [];
    
    // Add all remaining VMs (not in failed list)
    const remainingVMs = instances?.filter(instance => {
      const serviceCatalogProductId = instance.provisioned_product_id?.value;
      const hostname = instance.provisioned_product_id?.hostname;
      
      return !failedVMs.some(fvm => 
        fvm.service_catalog_product_id === serviceCatalogProductId || 
        fvm.hostname === hostname
      );
    }) || [];
    
    remainingVMs.forEach(instance => {
      const serviceCatalogProductId = instance.provisioned_product_id?.value;
      const isRemoved = nonExistingProducts.includes(serviceCatalogProductId);
      
      statuses.push({
        label: instance.provisioned_product_id?.label || 'Unknown',
        hostname: instance.provisioned_product_id?.hostname || 'Unknown',
        status: isRemoved ? 'Removed' : 'Succeeded',
        reason: isRemoved ? 'Product removed/not available' : 'N/A'
      });
    });
    
    return statuses;
  }, [status, failed_instances, instances, nonExisting]);

  if (!hasRatio) {
    return <span>{status}</span>;
  }

  return (
    <>
      <span>
        {status.replace(/\(\d+\/\d+\)/, '')}
        <Button 
          variant="link" 
          className="p-0 text-decoration-underline"
          onClick={handleShow}
          style={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
        >
          ({succeeded}/{total})
        </Button>
      </span>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>VM Status Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Badge variant="success" className="me-2">{succeeded} Succeeded</Badge>
            <Badge variant="danger" className="me-2">{failed} Failed</Badge>
            {nonExisting && <Badge variant="warning">{nonExisting.split(',').length} Removed</Badge>}
          </div>
          
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>VM Label</th>
                <th>Hostname</th>
                <th>Status</th>
                <th>Failure Reason</th>
              </tr>
            </thead>
            <tbody>
              {vmStatuses.map((vm, index) => (
                <tr key={index}>
                  <td>{vm.label}</td>
                  <td>{vm.hostname}</td>
                  <td>
                    <Badge variant={vm.status === 'Succeeded' ? 'success' : vm.status === 'Removed' ? 'warning' : 'danger'}>
                      {vm.status}
                    </Badge>
                  </td>
                  <td>{vm.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VMStatusModal;